import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { IFormThingsToImprove } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { normalizeBusinessPriorityFormValues } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/businessPriorities/helpers'
import { serializeDescriptionQualityResult } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/mappers'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { normalizeKeyInitiativeStatus } from 'partner360/entities/keyInitiatives/model/status'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IKeyInitiativeForm } from '../models/keyInitiativeForm'

import {
  getIsPracticeDevelopment,
  getPracticeDevelopmentProgressValues,
} from './getPracticeDevelopmentProgressValues'
import { EKeyInitiativeGoalMode } from './keyInitiativeGoalMode'
import { normalizeKeyInitiativeOutcome } from './keyInitiativeOutcome'

const NO_SALES_TARGET_VALUES: Partial<IFormThingsToImprove> = {
  AddToBridges: 0,
  GoalNdp: 0,
  GoalNdpUSD: 0,
  GoalNdpLocalCurrency: 0,
  GoalNet: 0,
  GoalNetUSD: 0,
  GoalNetLocalCurrency: 0,
  GoalTNet: 0,
  GoalTNetUSD: 0,
  GoalTNetLocalCurrency: 0,
  GoalUTNet: 0,
  GoalUTNetUSD: 0,
  GoalUTNetLocalCurrency: 0,
  IncrementalNdp: 0,
  IncrementalNdpUSD: 0,
  IncrementalNdpLocalCurrency: 0,
  IncrementalNet: 0,
  IncrementalNetUSD: 0,
  IncrementalNetLocalCurrency: 0,
  IncrementalTNet: 0,
  IncrementalTNetUSD: 0,
  IncrementalTNetLocalCurrency: 0,
  IncrementalUTNet: 0,
  IncrementalUTNetUSD: 0,
  IncrementalUTNetLocalCurrency: 0,
}

export const mapFormToKeyInitiative = (
  formData: IKeyInitiativeForm,
  isInvestmentNeeds: boolean,
  businessPriorities: IKIBusinessPriority[] = [],
  keyObjectives: IKeyObjective[] = []
) => {
  const isPracticeDevelopment = getIsPracticeDevelopment(
    formData.keyObjective,
    keyObjectives,
    formData.isPracticeDevelopment
  )
  const progressValues = isPracticeDevelopment
    ? getPracticeDevelopmentProgressValues(formData.currentProgress)
    : null

  const result: Partial<IFormThingsToImprove> = {
    EntryId: formData.entryId,
    PreviousEntryId: formData.previousEntryId,
    IsNotApplicable: Number(formData.isNotApplicable),
    Status: normalizeKeyInitiativeStatus(formData.status),
    Name: formData.name || '',
    Development: formData.keyObjective,
    BusinessPriorities: normalizeBusinessPriorityFormValues(
      formData.businessPriorities || [],
      businessPriorities
    ).join(','),
    Period: formData.period.join(','),
    Owner: formData.owner,
    ActivityType: formData.activityType,
    AddToBridges: formData.shouldAddToBridges ? 1 : 0,
    DataInsightRelated: formData.isDataInsightRelated ? 1 : 0,
    Currency: formData.currency,
    ValueType: formData.valueType,
    Outcome: normalizeKeyInitiativeOutcome(formData.outcome),
    OutcomeValueType: formData.outcomeValueType,
    FunnelSize: formData.funnelSize,
    Leads: formData.leads,
    ConversionRate: formData.conversionRate,
    Attendance: formData.attendance,
    BusinessUnit: formData.businessUnit,
    PracticeBuilder: formData.practiceBuilder,
    FocusArea: formData.focusArea,
    Description: formData.description,
    DescriptionQuality: serializeDescriptionQualityResult(formData.descriptionQuality),
    CurrentProgress: progressValues?.currentProgress ?? formData.currentProgress,
    FollowUp: progressValues?.followUp ?? formData.followUp,
    HPInvestmentSum: formData.hpInvestmentSum,
    HPInvestmentSource: formData.hpInvestmentSource,
    InitType: formData.initType,
    GoalDescription: formData.goalDescription,
    Justification: formData.justification,
    ProofOfExecution: formData.proofOfExecution,
    IsAchieved: formData.isAchieved ? 1 : 0,
    KeyObjectiveTags: formData.keyObjectiveTags.join(','),
  }

  if (formData.isAchieved === null) {
    result.IsAchieved = null
  }

  switch (formData.valueType) {
    // TODO: ActualsValueTypes.UTNet
    case 'UTNet':
      result.GoalUTNet = formData.goalUTNet
      result.GoalUTNetUSD = formData.goalUTNetUsd
      result.GoalUTNetLocalCurrency = formData.goalUTNetLocalCurrency
      result.IncrementalUTNet = formData.incrementalUTNet
      result.IncrementalUTNetUSD = formData.incrementalUTNetUsd
      result.IncrementalUTNetLocalCurrency = formData.incrementalUTNetLocalCurrency
      break

    case ActualsValueTypes.TNet:
      result.GoalTNet = formData.goalTNet
      result.GoalTNetUSD = formData.goalTNetUsd
      result.GoalTNetLocalCurrency = formData.goalTNetLocalCurrency
      result.IncrementalTNet = formData.incrementalTNet
      result.IncrementalTNetUSD = formData.incrementalTNetUsd
      result.IncrementalTNetLocalCurrency = formData.incrementalTNetLocalCurrency
      break

    case ActualsValueTypes.Net:
      result.GoalNet = formData.goalNet
      result.GoalNetUSD = formData.goalNetUsd
      result.GoalNetLocalCurrency = formData.goalNetLocalCurrency
      result.IncrementalNet = formData.incrementalNet
      result.IncrementalNetUSD = formData.incrementalNetUsd
      result.IncrementalNetLocalCurrency = formData.incrementalNetLocalCurrency
      break

    default:
    case ActualsValueTypes.Ndp:
      result.GoalNdp = formData.goalNdp
      result.GoalNdpUSD = formData.goalNdpUsd
      result.GoalNdpLocalCurrency = formData.goalNdpLocalCurrency
      result.IncrementalNdp = formData.incrementalNdp
      result.IncrementalNdpUSD = formData.incrementalNdpUsd
      result.IncrementalNdpLocalCurrency = formData.incrementalNdpLocalCurrency
      break
  }

  if (formData.goalMode === EKeyInitiativeGoalMode.NoSalesTarget) {
    Object.assign(result, NO_SALES_TARGET_VALUES)
  }

  if (!isInvestmentNeeds) {
    delete result.HPInvestmentSum
    delete result.HPInvestmentSource
  }

  return result
}
